import { api } from 'api/api';
import {
    PaymentDetailsDto,
    SubscriptionHistoryDto,
    SubscriptionPlanDto,
    UserSubscriptionDto,
    BillingAddressDto,
} from 'api/dtos/subscriptionPlanDtos';

export interface SubscribeRequest {
    subscriptionPlanId: string;
    paymentDetails: PaymentDetailsDto; 
}

export interface UpgradeRequest {
    newPlanId: string;
}
export interface AddPaymentMethodRequest {
  paymentDetails: PaymentDetailsDto;
  setAsDefault: boolean;
}

export const subscriptionPlanApi = api.injectEndpoints({
    endpoints: (builder) => ({
        getActivePlans: builder.query<SubscriptionPlanDto[], void>({
            query: () => 'subscriptionplan/active',
            transformResponse: (response: { success: boolean; data: SubscriptionPlanDto[] }) => response.data,
        }),
        getPlanById: builder.query<SubscriptionPlanDto, string>({
            query: (id) => `subscriptionplan/${id}`,
            transformResponse: (response: { success: boolean; data: SubscriptionPlanDto }) => response.data,
        }),
        getPlanByName: builder.query<SubscriptionPlanDto, string>({
            query: (name) => `subscriptionplan/name/${name}`,
            transformResponse: (response: { success: boolean; data: SubscriptionPlanDto }) => response.data,
        }),
        getFreePlan: builder.query<SubscriptionPlanDto, void>({
            query: () => 'subscriptionplan/free',
            transformResponse: (response: { success: boolean; data: SubscriptionPlanDto }) => response.data,
        }),
        subscribe: builder.mutation<{ subscriptionId: string; message: string }, SubscribeRequest>({
            query: (body) => ({
                url: 'subscriptionplan/subscribe',
                method: 'POST',
                body,
            }),
            invalidatesTags: ['Subscription', 'PaymentMethods'],
            extraOptions: { 
                successMessage: 'Subskrypcja została aktywowana.',
                errorMessage: 'Nie udało się aktywować subskrypcji.',
            },
        }),
        unsubscribe: builder.mutation<{ message: string }, void>({
            query: () => ({
                url: 'subscriptionplan/unsubscribe',
                method: 'POST',
            }),
            invalidatesTags: ['Subscription', 'PaymentMethods'],
            extraOptions: { 
                successMessage: 'Subskrypcja została anulowana.',
                errorMessage: 'Nie udało się anulować subskrypcji.',
            },
        }),
        getMySubscriptions: builder.query<UserSubscriptionDto[], void>({
            query: () => 'subscriptionplan/my-subscription',
            providesTags: ['Subscription'],
            transformResponse: (response: { hasSubscription: boolean; data: UserSubscriptionDto[] | UserSubscriptionDto | null }) => {
                if (Array.isArray(response.data)) {
                    return response.data;
                }

                return response.data ? [response.data] : [];
            },
        }),
        refreshMySubscription: builder.mutation<{ message: string }, void>({
            query: () => ({
                url: 'subscriptionplan/my-subscription/refresh',
                method: 'POST',
            }),
            extraOptions: {
                successMessage: 'Stan subskrypcji został odświeżony.',
                errorMessage: 'Nie udało się odświeżyć stanu subskrypcji.',
            },
        }),
        hasActiveSubscription: builder.query<boolean, void>({
            query: () => 'subscriptionplan/has-active',
            transformResponse: (response: { success: boolean; hasActiveSubscription: boolean }) => response.hasActiveSubscription,
            providesTags: ['Subscription'],
        }),
        getSubscriptionHistory: builder.query<SubscriptionHistoryDto[], void>({
            query: () => 'subscriptionplan/history',
            transformResponse: (response: { success: boolean; data: SubscriptionHistoryDto[] }) => response.data,
        }),
        getPaymentMethods: builder.query<PaymentDetailsDto[], void>({
            query: () => 'subscriptionplan/payment-methods',
            transformResponse: (response: { success: boolean; data: PaymentDetailsDto[] }) => response.data,
            providesTags: ['PaymentMethods'],
        }),
        updatePaymentMethodBilling: builder.mutation<void, { paymentMethodId: string; billingAddress: BillingAddressDto }>({
            query: ({ paymentMethodId, billingAddress }) => ({
                url: `subscriptionplan/payment-methods/${paymentMethodId}/billing`,
                method: 'PUT',
                body: billingAddress,
            }),
            invalidatesTags: ['PaymentMethods'],
            extraOptions: { 
                successMessage: 'Adres rozliczeniowy został zaktualizowany.',
                errorMessage: 'Nie udało się zaktualizować adresu rozliczeniowego.',
            },
        }),
        removePaymentMethod: builder.mutation<void, string>({
            query: (paymentMethodId) => ({
                url: `subscriptionplan/payment-methods/${paymentMethodId}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['PaymentMethods'],
            extraOptions: { 
                successMessage: 'Metoda płatności została usunięta.',
                errorMessage: 'Nie udało się usunąć metody płatności.',
            },
        }),
        removeAllPaymentMethods: builder.mutation<void, void>({
            query: () => ({
                url: 'subscriptionplan/payment-methods',
                method: 'DELETE',
            }),
            invalidatesTags: ['PaymentMethods'],
            extraOptions: { 
                successMessage: 'Wszystkie metody płatności zostały usunięte.',
                errorMessage: 'Nie udało się usunąć metod płatności.',
            },
        }),
        addPaymentMethod: builder.mutation<void, AddPaymentMethodRequest>({
            query: (body) => ({
                url: 'subscriptionplan/payment-methods',
                method: 'POST',
                body,
            }),
            invalidatesTags: ['PaymentMethods'],
            extraOptions: { 
                successMessage: 'Metoda płatności została dodana.',
                errorMessage: 'Nie udało się dodać metody płatności.',
            },
        }),
    }),
    overrideExisting: false,
});

export const {
    useGetActivePlansQuery,
    useGetPlanByIdQuery,
    useGetPlanByNameQuery,
    useGetFreePlanQuery,
    useSubscribeMutation,
    useUnsubscribeMutation,
    useGetMySubscriptionsQuery,
    useRefreshMySubscriptionMutation,
    useHasActiveSubscriptionQuery,
    useGetSubscriptionHistoryQuery,
    useAddPaymentMethodMutation,
    useGetPaymentMethodsQuery,
    useUpdatePaymentMethodBillingMutation,
    useRemovePaymentMethodMutation,
    useRemoveAllPaymentMethodsMutation,
} = subscriptionPlanApi;
